import Driver, { removerResiduoDeTour } from '../../plugins/driver.js'
import { rotaCasaPattern } from '../utils/rota_matcher.js'
import { TOUR_PLAYER_STATE_KEY, TOUR_BUILDER_ATIVO_KEY } from '../utils/storage_keys.js'
import { limparPopupsLiberados, sincronizarPopupsLiberados } from '../utils/popups_liberados.js'
import {
  aplicarAlertaDeEnvio,
  ehBotaoDeEnvio,
  ehPassoDeEnvio,
  envioFalhou,
  errosDeEnvio,
  modalDoAlvo
} from '../utils/passo_de_envio.js'
import {
  ACOES_DO_TOUR,
  MOTIVOS_DE_INTERRUPCAO,
  montarDadosDaInterrupcao
} from '../utils/motivos_de_interrupcao.js'

const CURINGA_ROTA = '*'
const SITUACAO_ROTA_ATUAL = 'atual'
const SITUACAO_ROTA_NAVEGANDO = 'navegando'
const SITUACAO_ROTA_INALCANCAVEL = 'inalcancavel'
const TEMPO_MAX_AGUARDA_ELEMENTO_MS = 1500
const TEMPO_ESTABILIZACAO_POS_DISPARO_MS = 300
const TEMPO_MAX_AGUARDA_NAVEGACAO_PELA_ACAO_MS = 3000
const MAX_REPOSICIONAMENTOS_EM_MODAL = 3
const SELETOR_MODAL_ABERTA = '.modal.show, .modal.in'
const SELETOR_MODAL_BACKDROP = '.modal-backdrop'
const CLASSE_BODY_MODAL_ABERTA = 'modal-open'
const CLASSES_MODAL_VISIVEL = ['show', 'in']
const ID_ELEMENTO_FANTASMA = 'driver-dummy-element'
const SELETORES_DRIVER = '.driver-popover, .driver-overlay, .driver-popover-content, #driver-dummy-element'
const CLASSE_BOTAO_AGUARDANDO = 'maino-tour-btn-aguardando'
const SELETOR_BOTAO_PROXIMO = '.maino-tour-popover .driver-popover-next-btn'
const SELETOR_BOTAO_ANTERIOR = '.maino-tour-popover .driver-popover-prev-btn'
const EVENTO_MODAL_FECHADA = 'hidden.bs.modal'
const EVENTO_ENVIO_FORMULARIO = 'submit'
const EVENTO_ENVIO_BARRADO_PELO_NAVEGADOR = 'invalid'

export function indiceRetomavel(estado, rotaAtual = window.location.pathname) {
  if (!Array.isArray(estado?.passos)) return null
  for (let indice = estado.indice_passo; indice < estado.passos.length; indice += 1) {
    const rotaDoPasso = estado.passos[indice]?.rota_pattern || estado.rota_pattern
    if (!rotaDoPasso || rotaCasaPattern(rotaAtual, rotaDoPasso)) return indice
  }
  return null
}

export function useTourPlayer(opcoes = {}) {
  let instanciaAtiva = null
  let suprimirSideEffects = false

  function builderAtivo() {
    try {
      return sessionStorage.getItem(TOUR_BUILDER_ATIVO_KEY) === '1'
    } catch (e) {
      console.warn('Falha ao consultar estado do builder:', e)
      return false
    }
  }

  function deveSuprimirSideEffects() {
    return builderAtivo() || suprimirSideEffects
  }

  function interromper(motivo, { tour, indicePasso, acao }, erroOriginal) {
    if (deveSuprimirSideEffects()) return
    try {
      opcoes.aoInterromper?.(motivo, montarDadosDaInterrupcao({ tour, indicePasso, acao }), erroOriginal)
    } catch (erroDoRegistro) {
      console.warn('Tour guiado: falha ao registrar interrupção:', erroDoRegistro)
    }
  }

  function encerrarSilenciosamente() {
    suprimirSideEffects = true
    try {
      if (instanciaAtiva?.isActive?.()) instanciaAtiva.destroy()
    } finally {
      suprimirSideEffects = false
      instanciaAtiva = null
      removerResiduoDeTour()
    }
  }

  function csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  function obterRotaDoPasso(passo, tour) {
    return passo.rota_pattern || tour.rota_pattern
  }

  function rotaInternaValida(rota) {
    if (typeof rota !== 'string' || rota.length === 0) return false
    if (!rota.startsWith('/')) return false
    if (rota.startsWith('//')) return false
    if (/^[a-z][a-z0-9+\-.]*:/i.test(rota)) return false
    return true
  }

  function rotaContemCuringa(rota) {
    return typeof rota === 'string' && rota.includes(CURINGA_ROTA)
  }

  function escreverEstado(payload) {
    try {
      sessionStorage.setItem(TOUR_PLAYER_STATE_KEY, JSON.stringify(payload))
    } catch (e) {
      console.warn('Falha ao persistir estado do tour:', e)
    }
  }

  function salvarEstado(tour, indicePasso, { navegacaoPeloTour = false } = {}) {
    const anterior = carregarEstado()
    const mesmoPasso = anterior && anterior.tour_id === tour.id && anterior.indice_passo === indicePasso
    const tentativasPreservadas = mesmoPasso ? tentativasDeNavegacao(anterior) : 0

    escreverEstado({
      tour_id: tour.id,
      indice_passo: indicePasso,
      passos: tour.passos,
      rota_pattern: tour.rota_pattern,
      nome: tour.nome,
      tentativas_navegacao: tentativasPreservadas,
      navegacao_pelo_tour: navegacaoPeloTour,
      atualizado_em: Date.now()
    })
  }

  function tentativasDeNavegacao(estado) {
    return typeof estado?.tentativas_navegacao === 'number' ? estado.tentativas_navegacao : 0
  }

  function registrarTentativaNavegacao(estado) {
    const tentativas = tentativasDeNavegacao(estado) + 1
    escreverEstado({ ...estado, tentativas_navegacao: tentativas, atualizado_em: Date.now() })
    return tentativas
  }

  function carregarEstado() {
    try {
      const bruto = sessionStorage.getItem(TOUR_PLAYER_STATE_KEY)
      if (!bruto) return null
      return JSON.parse(bruto)
    } catch (e) {
      console.warn('Falha ao ler estado do tour:', e)
      return null
    }
  }

  function limparEstado() {
    try {
      sessionStorage.removeItem(TOUR_PLAYER_STATE_KEY)
    } catch (e) {
      console.warn('Falha ao limpar estado do tour:', e)
    }
  }

  async function marcarConclusao(tourId, pulou) {
    try {
      await fetch('/tour_conclusoes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken()
        },
        body: JSON.stringify({ tour_guiado_id: tourId, pulou })
      })
    } catch (e) {
      console.error('Falha ao registrar conclusão do tour:', e)
    } finally {
      limparEstado()
    }
  }

  function elementoVisivel(el) {
    if (!el) return false
    const rect = el.getBoundingClientRect()
    if (rect.width === 0 && rect.height === 0) return false
    const estilo = window.getComputedStyle(el)
    if (estilo.visibility === 'hidden' || estilo.display === 'none') return false
    if (parseFloat(estilo.opacity) === 0) return false
    return true
  }

  function dentroDeModal(el) {
    return Boolean(el?.closest?.('.modal'))
  }

  function modalAberta() {
    return document.querySelector(SELETOR_MODAL_ABERTA)
  }

  function modaisAbertasAgora() {
    return new Set(document.querySelectorAll(SELETOR_MODAL_ABERTA))
  }

  function modalAindaVisivel(modal) {
    return CLASSES_MODAL_VISIVEL.some((classe) => modal.classList.contains(classe))
  }

  function algumaModalOcupandoATela() {
    if (modalAberta()) return true
    return Array.from(document.querySelectorAll('.modal')).some((modal) => modal.style.display === 'block')
  }

  function removerBackdropSemModalAberta() {
    if (algumaModalOcupandoATela()) return
    document.querySelectorAll(SELETOR_MODAL_BACKDROP).forEach((no) => no.remove())
    document.body.classList.remove(CLASSE_BODY_MODAL_ABERTA)
  }

  function fecharModalPelaBiblioteca(modal) {
    try {
      const instancia = window.bootstrap?.Modal?.getInstance?.(modal)
      if (instancia?.hide) {
        instancia.hide()
        return
      }
      const jquery = window.jQuery || window.$
      jquery?.(modal)?.modal?.('hide')
    } catch (e) {
      console.warn('Tour: falha ao fechar modal pela biblioteca:', e)
    }
  }

  function fecharModalManualmente(modal) {
    modal.classList.remove(...CLASSES_MODAL_VISIVEL)
    modal.style.display = 'none'
    modal.setAttribute('aria-hidden', 'true')
    removerBackdropSemModalAberta()
  }

  function removerFantasmaOrfao(elementoDestacado = null) {
    const fantasma = document.getElementById(ID_ELEMENTO_FANTASMA)
    if (!fantasma || fantasma === elementoDestacado) return
    if (!elementoDestacado && fantasma.classList.contains('driver-active-element')) return
    fantasma.remove()
  }

  function resolverElementoNoContexto(seletor) {
    if (!seletor) return null
    const todos = document.querySelectorAll(seletor)
    if (todos.length === 0) return null
    if (todos.length === 1) return todos[0]

    const modaisAbertos = Array.from(document.querySelectorAll('.modal.show, .modal.in'))
    for (const modal of modaisAbertos) {
      for (const el of todos) {
        if (modal.contains(el) && elementoVisivel(el)) return el
      }
    }

    for (const el of todos) {
      if (elementoVisivel(el)) return el
    }

    return todos[0]
  }

  function resolverAlvoVisivel(seletor) {
    const el = resolverElementoNoContexto(seletor)
    return elementoVisivel(el) ? el : null
  }

  function alvoPresenteMasInvisivel(seletor) {
    if (!seletor) return false
    const el = resolverElementoNoContexto(seletor)
    return Boolean(el) && !elementoVisivel(el)
  }

  function aguardarElementoVisivel(seletor, timeout = TEMPO_MAX_AGUARDA_ELEMENTO_MS) {
    return new Promise((resolve) => {
      const inicio = Date.now()
      let rectAnterior = null
      let estavelFrames = 0

      const tentar = () => {
        const el = resolverElementoNoContexto(seletor)
        if (!elementoVisivel(el)) {
          if (Date.now() - inicio >= timeout) return resolve(null)
          rectAnterior = null
          estavelFrames = 0
          return requestAnimationFrame(tentar)
        }

        const rect = el.getBoundingClientRect()
        const minimoEstavel = dentroDeModal(el) ? 4 : 2
        const estavel = rectAnterior &&
          Math.abs(rect.top - rectAnterior.top) < 1 &&
          Math.abs(rect.left - rectAnterior.left) < 1

        if (estavel) {
          estavelFrames += 1
          if (estavelFrames >= minimoEstavel) return resolve(el)
        } else {
          estavelFrames = 0
        }
        rectAnterior = rect

        if (Date.now() - inicio >= timeout) return resolve(el)
        requestAnimationFrame(tentar)
      }
      tentar()
    })
  }

  function elementoDoRecord(record) {
    const alvo = record.target
    return alvo?.nodeType === 1 ? alvo : alvo?.parentElement
  }

  function classesAlteradasSaoDoDriver(record) {
    const antes = String(record.oldValue || '').split(/\s+/).filter(Boolean)
    const depois = Array.from(elementoDoRecord(record)?.classList || [])
    const alteradas = antes
      .filter((classe) => !depois.includes(classe))
      .concat(depois.filter((classe) => !antes.includes(classe)))
    return alteradas.every((classe) => classe.startsWith('driver-'))
  }

  function noPertenceAoDriver(no) {
    if (no?.nodeType !== 1) return false
    return Boolean(no.matches(SELETORES_DRIVER) || no.closest(SELETORES_DRIVER))
  }

  function mutacaoOriginadaPeloDriver(record) {
    const elemento = elementoDoRecord(record)
    if (elemento?.closest?.(SELETORES_DRIVER)) return true

    if (record.type === 'attributes') {
      if (record.attributeName === 'class') return classesAlteradasSaoDoDriver(record)
      return false
    }

    const nos = [...record.addedNodes, ...record.removedNodes]
    return nos.length > 0 && nos.every(noPertenceAoDriver)
  }

  function instalarRefreshEmMutacoes(driverInstance, janelaTransicao, reancorar) {
    const refresh = () => {
      if (driverInstance.isActive?.()) {
        sincronizarPopupsLiberados()
        if (reancorar?.()) return
        try { driverInstance.refresh() } catch (_e) { /* noop */ }
      }
    }

    let agendado = false
    const refreshOuAdiar = () => {
      if (janelaTransicao.emCurso()) {
        janelaTransicao.marcarPendente()
        return
      }
      if (agendado) return
      agendado = true
      requestAnimationFrame(() => {
        agendado = false
        refresh()
      })
    }

    const observer = new MutationObserver((records) => {
      if (records.every(mutacaoOriginadaPeloDriver)) return
      refreshOuAdiar()
    })
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeOldValue: true,
      attributeFilter: ['class', 'style', 'aria-hidden']
    })

    const modalHandler = (evento) => {
      refresh()
      requestAnimationFrame(refresh)
      setTimeout(refresh, 350)
      if (evento?.type === 'shown.bs.modal' && evento.target) {
        const alvo = evento.target
        alvo.addEventListener('transitionend', refresh, { once: true })
      }
    }
    const eventosModal = ['shown.bs.modal', 'hidden.bs.modal', 'shown.bs.dropdown', 'shown.bs.collapse']
    eventosModal.forEach((evt) => document.addEventListener(evt, modalHandler))

    const aoRolarContainer = (evento) => {
      if (evento.target === document) return
      refreshOuAdiar()
    }
    window.addEventListener('scroll', aoRolarContainer, true)

    return {
      flush: refresh,
      disconnect() {
        observer.disconnect()
        eventosModal.forEach((evt) => document.removeEventListener(evt, modalHandler))
        window.removeEventListener('scroll', aoRolarContainer, true)
      }
    }
  }

  function montarPassosDriver(passos) {
    return passos.map((passo) => ({
      element: () => resolverAlvoVisivel(passo.seletor_alvo),
      popover: {
        title: passo.titulo,
        description: passo.descricao,
        side: passo.posicionamento,
        align: 'start'
      }
    }))
  }

  function disparoSimulado(passo) {
    if (!passo.seletor_alvo) return
    const alvo = resolverElementoNoContexto(passo.seletor_alvo)
    if (!alvo) return

    if (passo.dispara_hover) {
      ;['mouseover', 'mouseenter'].forEach((tipo) => {
        alvo.dispatchEvent(new MouseEvent(tipo, { bubbles: true, cancelable: true, view: window }))
      })
    }
    if (passo.dispara_clique) {
      if (typeof alvo.click === 'function') {
        alvo.click()
      } else {
        alvo.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }))
      }
    }
  }

  async function aguardarAlvoDoPasso(passo) {
    if (!passo?.seletor_alvo) return null
    const alvo = await aguardarElementoVisivel(passo.seletor_alvo)
    return alvo
  }

  const botoesAguardando = new Map()

  function ligarBotaoAguardando(seletor) {
    const botao = document.querySelector(seletor)
    if (!botao) return
    botoesAguardando.set(seletor, { botao, desabilitadoAntes: botao.disabled })
    botao.classList.add(CLASSE_BOTAO_AGUARDANDO)
    botao.disabled = true
  }

  function desligarBotaoAguardando(seletor) {
    const registro = botoesAguardando.get(seletor)
    if (!registro) return
    botoesAguardando.delete(seletor)
    registro.botao.classList.remove(CLASSE_BOTAO_AGUARDANDO)
    registro.botao.disabled = registro.desabilitadoAntes
  }

  function alternarBotaoAguardando(seletor, aguardando) {
    if (aguardando) {
      ligarBotaoAguardando(seletor)
      return
    }
    desligarBotaoAguardando(seletor)
  }

  function encerrarPorFalhaAoIniciar(erro, contexto) {
    instanciaAtiva = null
    limparEstado()
    console.error('Tour guiado: falha ao iniciar o driver. Tour encerrado e estado limpo.', erro)
    interromper(MOTIVOS_DE_INTERRUPCAO.FALHA_AO_INICIAR, contexto, erro)
  }

  function iniciarDriver(tour, indiceInicial, acao) {
    try {
      montarDriverDoTour(tour, indiceInicial, acao)
    } catch (erro) {
      encerrarPorFalhaAoIniciar(erro, { tour, indicePasso: indiceInicial, acao })
    }
  }

  function montarDriverDoTour(tour, indiceInicial, acao) {
    encerrarSilenciosamente()

    let acaoEmCurso = acao
    const interromperNoPasso = (motivo, indicePasso) =>
      interromper(motivo, { tour, indicePasso, acao: acaoEmCurso })

    let registrado = false
    let navegando = false
    let observer = null
    let reposicionandoModal = false
    let transicaoEmCurso = false
    let refreshPendentePosTransicao = false
    let aguardandoNavegacao = false
    let pulandoPasso = false
    let algumPassoDestacado = false
    let abandonandoTour = false
    let pulosRestantes = Array.isArray(tour.passos) ? tour.passos.length : 0
    let reposicionamentosRestantes = MAX_REPOSICIONAMENTOS_EM_MODAL
    let indiceComOrcamentoDeReposicionamento = null
    let modalAbertaPeloTour = null
    let listenerCliqueReversoAtual = null
    let disparoProgramadoEmCurso = false
    let reancorandoPasso = false
    let indiceJaReancorado = null
    let ultimoIndiceDestacado = null
    let estadoEnvioRenderizado = null
    let vigiaFechamentoModal = null
    let envioTentado = null

    const janelaTransicao = {
      emCurso: () => transicaoEmCurso,
      marcarPendente: () => {
        refreshPendentePosTransicao = true
      }
    }

    const encerrarJanelaTransicao = () => {
      transicaoEmCurso = false
      if (!refreshPendentePosTransicao) return
      refreshPendentePosTransicao = false
      requestAnimationFrame(() => observer?.flush())
    }

    const registrar = (pulou) => {
      if (registrado) return
      registrado = true
      marcarConclusao(tour.id, pulou)
    }

    const resolverRotaDoPasso = (indicePasso) => {
      const passo = tour.passos[indicePasso]
      if (!passo) return SITUACAO_ROTA_ATUAL

      const rotaPasso = obterRotaDoPasso(passo, tour)
      if (!rotaPasso) return SITUACAO_ROTA_ATUAL
      if (rotaCasaPattern(window.location.pathname, rotaPasso)) return SITUACAO_ROTA_ATUAL

      if (!rotaInternaValida(rotaPasso)) {
        console.warn('Tour: rota_pattern invalida ignorada por seguranca:', rotaPasso)
        interromperNoPasso(MOTIVOS_DE_INTERRUPCAO.ROTA_INVALIDA, indicePasso)
        return SITUACAO_ROTA_INALCANCAVEL
      }
      if (rotaContemCuringa(rotaPasso)) {
        console.warn('Tour: rota_pattern com curinga nao pode ser destino de navegacao:', rotaPasso)
        interromperNoPasso(MOTIVOS_DE_INTERRUPCAO.ROTA_COM_CURINGA, indicePasso)
        return SITUACAO_ROTA_INALCANCAVEL
      }

      navegando = true
      salvarEstado(tour, indicePasso, { navegacaoPeloTour: true })
      if (driver.driverInstance.isActive?.()) driver.driverInstance.destroy()
      window.location.href = rotaPasso
      return SITUACAO_ROTA_NAVEGANDO
    }

    const reancorarPassoNoFantasma = () => {
      if (reancorandoPasso) return false
      const instancia = driver.driverInstance
      if (!document.getElementById(ID_ELEMENTO_FANTASMA)) return false
      const indiceAtual = instancia.getActiveIndex?.()
      if (typeof indiceAtual !== 'number') return false
      if (indiceJaReancorado === indiceAtual) return false
      const passoAtual = tour.passos[indiceAtual]
      if (!resolverAlvoVisivel(passoAtual?.seletor_alvo)) return false
      reancorandoPasso = true
      indiceJaReancorado = indiceAtual
      try {
        instancia.moveTo(indiceAtual)
      } catch (_e) {
        return false
      } finally {
        requestAnimationFrame(() => { reancorandoPasso = false })
      }
      return true
    }

    const envioEmAlerta = (indice) =>
      Boolean(estadoEnvioRenderizado?.emAlerta && estadoEnvioRenderizado.indice === indice)

    const envioDoPassoFalhou = (passo) => envioFalhou(resolverElementoNoContexto(passo?.seletor_alvo))

    const errosNovosDesdeOEnvio = (alvo) => {
      if (!envioTentado) return false
      return errosDeEnvio(alvo).some((erro) => !envioTentado.errosAntes.has(erro))
    }

    const alertaNoPasso = (alvo, indice, alertaAtual) => {
      if (envioTentado?.indice !== indice) return false
      if (errosNovosDesdeOEnvio(alvo)) return true
      return alertaAtual
    }

    const avancoDoEnvioComprovado = (passoAtual, passoDestino, alvoDestino) => {
      if (alvoDestino) return true
      if (passoDestino?.seletor_alvo) return false
      return !envioDoPassoFalhou(passoAtual)
    }

    const reancorarPassoDeEnvio = () => {
      if (reancorandoPasso || !estadoEnvioRenderizado) return false
      const instancia = driver.driverInstance
      const indiceAtual = instancia.getActiveIndex?.()
      if (estadoEnvioRenderizado.indice !== indiceAtual) return false
      const alvo = resolverAlvoVisivel(tour.passos[indiceAtual]?.seletor_alvo)
      if (!ehPassoDeEnvio(alvo)) return false
      const elementoMudou = alvo !== instancia.getActiveElement?.()
      const alertaAtual = estadoEnvioRenderizado.emAlerta
      const alertaMudou = alertaNoPasso(alvo, indiceAtual, alertaAtual) !== alertaAtual
      if (!elementoMudou && !alertaMudou) return false
      reancorandoPasso = true
      try {
        instancia.moveTo(indiceAtual)
      } catch (_e) {
        return false
      } finally {
        requestAnimationFrame(() => { reancorandoPasso = false })
      }
      return true
    }

    const reancorarPasso = () => reancorarPassoNoFantasma() || reancorarPassoDeEnvio()

    const desarmarVigiaFechamento = () => {
      if (!vigiaFechamentoModal) return
      const { modal, aoFechar, aoEnviar } = vigiaFechamentoModal
      vigiaFechamentoModal = null
      const jquery = window.jQuery || window.$
      jquery?.(modal)?.off?.(EVENTO_MODAL_FECHADA, aoFechar)
      jquery?.(modal)?.off?.(EVENTO_ENVIO_FORMULARIO, 'form', aoEnviar)
    }

    const seguirAposEnvioQueFechouModal = async () => {
      if (aguardandoNavegacao) return
      const instancia = driver.driverInstance
      const indiceAtual = instancia.getActiveIndex?.()
      const alvoSeguinte = await aguardarAlvoDoPasso(tour.passos[indiceAtual + 1])
      if (!alvoSeguinte || !instancia.isActive?.()) return
      if (instancia.getActiveIndex?.() !== indiceAtual) return
      instancia.moveNext()
    }

    const armarVigiaFechamento = (modal) => {
      const jquery = window.jQuery || window.$
      if (!modal || typeof jquery !== 'function') return
      const vigia = { modal, envioEmAndamento: false }
      vigia.aoEnviar = () => {
        vigia.envioEmAndamento = true
      }
      vigia.aoFechar = () => {
        desarmarVigiaFechamento()
        if (!driver.driverInstance.isActive?.()) return
        if (vigia.envioEmAndamento) {
          seguirAposEnvioQueFechouModal()
          return
        }
        interromperNoPasso(
          MOTIVOS_DE_INTERRUPCAO.MODAL_FECHADA_COM_ERRO_DE_ENVIO,
          driver.driverInstance.getActiveIndex?.()
        )
        if (!deveSuprimirSideEffects()) registrar(true)
        abandonarTour()
      }
      jquery(modal).on(EVENTO_MODAL_FECHADA, vigia.aoFechar)
      jquery(modal).on(EVENTO_ENVIO_FORMULARIO, 'form', vigia.aoEnviar)
      vigiaFechamentoModal = vigia
    }

    const aoRenderizarBalao = (popover, opcoesRender) => {
      desarmarVigiaFechamento()
      const renderAnterior = estadoEnvioRenderizado
      estadoEnvioRenderizado = null
      const alvo = driver.driverInstance.getActiveElement?.()
      if (!ehPassoDeEnvio(alvo)) return
      const indice = opcoesRender?.state?.activeIndex
      const alertaAtual = renderAnterior?.indice === indice && renderAnterior.emAlerta
      const emAlerta = alertaNoPasso(alvo, indice, alertaAtual)
      estadoEnvioRenderizado = { indice, emAlerta }
      if (!emAlerta) return
      interromperNoPasso(MOTIVOS_DE_INTERRUPCAO.ERRO_NO_ENVIO_DO_FORMULARIO, indice)
      aplicarAlertaDeEnvio(popover)
      armarVigiaFechamento(modalDoAlvo(alvo))
    }

    const aoEnviarFormulario = (evento) => {
      const instancia = driver.driverInstance
      if (!instancia.isActive?.()) return
      const alvo = instancia.getActiveElement?.()
      if (!ehPassoDeEnvio(alvo) || !evento.target?.contains?.(alvo)) return
      envioTentado = { indice: instancia.getActiveIndex?.(), errosAntes: new Set(errosDeEnvio(alvo)) }
    }

    const aoBarrarEnvioNoNavegador = (evento) => {
      const instancia = driver.driverInstance
      if (!instancia.isActive?.()) return
      const alvo = instancia.getActiveElement?.()
      if (!ehPassoDeEnvio(alvo) || evento.target?.form !== alvo.form) return
      const indice = instancia.getActiveIndex?.()
      if (envioTentado?.indice !== indice) envioTentado = { indice, errosAntes: new Set() }
      requestAnimationFrame(reancorarPassoDeEnvio)
    }

    const reposicionarPasso = async (passoAtual, indiceAtual, ehDummy) => {
      if (!passoAtual?.seletor_alvo) return true
      if (reposicionandoModal) return true
      reposicionandoModal = true
      try {
        if (indiceComOrcamentoDeReposicionamento !== indiceAtual) {
          indiceComOrcamentoDeReposicionamento = indiceAtual
          reposicionamentosRestantes = MAX_REPOSICIONAMENTOS_EM_MODAL
        }
        let alvoAguardado = await aguardarElementoVisivel(passoAtual.seletor_alvo)
        while (!alvoAguardado && algumaModalOcupandoATela() && reposicionamentosRestantes > 0) {
          reposicionamentosRestantes -= 1
          alvoAguardado = await aguardarElementoVisivel(passoAtual.seletor_alvo)
        }
        if (!driver.driverInstance.isActive?.()) return true
        if (driver.driverInstance.getActiveIndex?.() !== indiceAtual) return true
        const el = resolverElementoNoContexto(passoAtual.seletor_alvo)
        if (!elementoVisivel(el)) return false
        if (ehDummy) {
          try { driver.driverInstance.moveTo(indiceAtual) } catch (_e) {
            try { driver.driverInstance.refresh() } catch (_e2) { /* noop */ }
          }
          removerFantasmaOrfao()
          return true
        }
        try { driver.driverInstance.refresh() } catch (_e) { /* noop */ }
        return true
      } finally {
        requestAnimationFrame(() => { reposicionandoModal = false })
      }
    }

    const abandonarTour = () => {
      abandonandoTour = true
      driver.driverInstance.destroy()
    }

    const encerrarTourPorRotaInalcancavel = () => {
      if (!deveSuprimirSideEffects()) {
        opcoes.aoRotaInalcancavel?.()
        registrar(true)
      }
      abandonarTour()
    }

    const registrarModalAbertaPeloTour = (modaisAntes) => {
      if (modalAbertaPeloTour?.isConnected) return
      const abertas = Array.from(document.querySelectorAll(SELETOR_MODAL_ABERTA))
      const nova = abertas.find((modal) => !modaisAntes.has(modal))
      if (nova) modalAbertaPeloTour = nova
    }

    const fecharModalAbertaPeloTour = () => {
      desarmarVigiaFechamento()
      const modal = modalAbertaPeloTour
      modalAbertaPeloTour = null
      if (!modal?.isConnected) return
      fecharModalPelaBiblioteca(modal)
      requestAnimationFrame(() => {
        if (!modal.isConnected) return removerBackdropSemModalAberta()
        if (modalAindaVisivel(modal)) fecharModalManualmente(modal)
        else removerBackdropSemModalAberta()
      })
    }

    const alvoDoPassoEstaNaModalDoTour = (passo) => {
      if (!modalAbertaPeloTour?.isConnected) return false
      const alvo = resolverElementoNoContexto(passo?.seletor_alvo)
      return Boolean(alvo && modalAbertaPeloTour.contains(alvo))
    }

    const passoAlcancavelAgora = (passo) => {
      if (!passo?.seletor_alvo) return true
      return elementoVisivel(resolverElementoNoContexto(passo.seletor_alvo))
    }

    const indiceAnteriorAlcancavel = (indice) => {
      for (let i = indice - 1; i >= 0; i -= 1) {
        if (passoAlcancavelAgora(tour.passos[i])) return i
      }
      return null
    }

    const restaurarContextoDoPasso = async (indice) => {
      const gatilho = tour.passos[indice - 1]
      const modaisAntes = modaisAbertasAgora()
      if (!dispararAcaoDoPasso(gatilho)) return null
      const alvo = await aguardarAlvoDoPasso(tour.passos[indice])
      registrarModalAbertaPeloTour(modaisAntes)
      return alvo
    }

    const indiceParaRetomar = async (indice) => {
      const ancora = indiceAnteriorAlcancavel(indice)
      if (ancora === null) return indice
      const abridor = tour.passos[ancora]
      if (ehBotaoDeEnvio(resolverElementoNoContexto(abridor?.seletor_alvo))) return ancora
      const modaisAntes = modaisAbertasAgora()
      if (!dispararAcaoDoPasso(abridor)) return ancora
      const alvo = await aguardarElementoVisivel(tour.passos[indice].seletor_alvo, TEMPO_MAX_AGUARDA_NAVEGACAO_PELA_ACAO_MS)
      registrarModalAbertaPeloTour(modaisAntes)
      if (alvo) return indice
      fecharModalAbertaPeloTour()
      return ancora
    }

    const encerrarTourPorAlvoInalcancavel = (motivo, indicePasso) => {
      interromperNoPasso(motivo, indicePasso)
      if (!deveSuprimirSideEffects()) opcoes.aoAlvoInalcancavel?.()
      fecharModalAbertaPeloTour()
      abandonarTour()
    }

    const fluxoDoPassoInterrompido = (indicePasso) => {
      const situacaoRota = resolverRotaDoPasso(indicePasso)
      if (situacaoRota === SITUACAO_ROTA_NAVEGANDO) return true
      if (situacaoRota !== SITUACAO_ROTA_INALCANCAVEL) return false
      encerrarTourPorRotaInalcancavel()
      return true
    }

    const navegacaoDependeDaAcaoDoPasso = (indicePasso) => {
      const passoAtual = tour.passos[indicePasso]
      const proximoPasso = tour.passos[indicePasso + 1]
      if (!passoAtual?.seletor_alvo || !proximoPasso || !passoAtual.dispara_clique) return false
      const rotaProxima = obterRotaDoPasso(proximoPasso, tour)
      if (!rotaContemCuringa(rotaProxima) || !rotaInternaValida(rotaProxima)) return false
      return !rotaCasaPattern(window.location.pathname, rotaProxima)
    }

    const aguardarNavegacaoPelaAcao = async (indicePasso) => {
      aguardandoNavegacao = true
      alternarBotaoAguardando(SELETOR_BOTAO_PROXIMO, true)
      try {
        dispararAcaoDoPasso(tour.passos[indicePasso])
        await new Promise((resolve) => setTimeout(resolve, TEMPO_MAX_AGUARDA_NAVEGACAO_PELA_ACAO_MS))
        if (!driver.driverInstance.isActive?.()) return
        const rotaProxima = obterRotaDoPasso(tour.passos[indicePasso + 1], tour)
        if (rotaCasaPattern(window.location.pathname, rotaProxima)) {
          driver.driverInstance.moveNext()
        } else {
          const motivo = envioDoPassoFalhou(tour.passos[indicePasso])
            ? MOTIVOS_DE_INTERRUPCAO.NAVEGACAO_BARRADA_POR_VALIDACAO
            : MOTIVOS_DE_INTERRUPCAO.NAVEGACAO_PELA_ACAO_NAO_OCORREU
          interromperNoPasso(motivo, indicePasso)
        }
      } finally {
        alternarBotaoAguardando(SELETOR_BOTAO_PROXIMO, false)
        aguardandoNavegacao = false
      }
    }

    const pularPasso = () => {
      if (pulandoPasso) return
      const instancia = driver.driverInstance
      if (!instancia.isActive?.()) return
      acaoEmCurso = ACOES_DO_TOUR.PULO
      const indiceAtual = instancia.getActiveIndex?.() ?? 0
      if (pulosRestantes <= 0) {
        interromperNoPasso(MOTIVOS_DE_INTERRUPCAO.PULOS_ESGOTADOS, indiceAtual)
        abandonarTour()
        return
      }
      if (!instancia.hasNextStep()) {
        encerrarTourPorAlvoInalcancavel(MOTIVOS_DE_INTERRUPCAO.ULTIMO_PASSO_SEM_ALVO, indiceAtual)
        return
      }
      const proximoIndice = indiceAtual + 1
      if (fluxoDoPassoInterrompido(proximoIndice)) return
      pulandoPasso = true
      pulosRestantes -= 1
      try {
        instancia.moveNext()
      } finally {
        pulandoPasso = false
      }
    }

    const limparListenerCliqueReverso = () => {
      if (!listenerCliqueReversoAtual) return
      const { elemento, handler } = listenerCliqueReversoAtual
      elemento.removeEventListener('click', handler)
      listenerCliqueReversoAtual = null
    }

    const registrarListenerCliqueReverso = (passo, indicePasso) => {
      limparListenerCliqueReverso()
      if (!passo.seletor_alvo || !passo.dispara_clique) return
      const alvo = resolverElementoNoContexto(passo.seletor_alvo)
      if (!alvo) return
      const handler = async () => {
        if (disparoProgramadoEmCurso) return
        if (!driver.driverInstance.isActive?.()) return
        if (driver.driverInstance.getActiveIndex?.() !== indicePasso) return
        if (aguardandoNavegacao) return
        if (navegacaoDependeDaAcaoDoPasso(indicePasso)) return
        acaoEmCurso = ACOES_DO_TOUR.CLIQUE_NO_ALVO
        if (fluxoDoPassoInterrompido(indicePasso + 1)) return
        const envioEmModal = ehPassoDeEnvio(alvo)
        const passoDestino = tour.passos[indicePasso + 1]
        aguardandoNavegacao = true
        try {
          const alvoDestino = await aguardarAlvoDoPasso(passoDestino)
          if (!driver.driverInstance.isActive?.()) return
          if (envioEmModal && !avancoDoEnvioComprovado(passo, passoDestino, alvoDestino)) return
          driver.driverInstance.moveNext()
        } finally {
          aguardandoNavegacao = false
        }
      }
      alvo.addEventListener('click', handler)
      listenerCliqueReversoAtual = { elemento: alvo, handler }
    }

    const dispararAcaoDoPasso = (passo) => {
      if (!passo?.seletor_alvo) return false
      if (!passo.dispara_clique && !passo.dispara_hover) return false
      disparoProgramadoEmCurso = true
      try {
        disparoSimulado(passo)
      } finally {
        disparoProgramadoEmCurso = false
      }
      return true
    }

    const reduzirMovimento = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false

    const driver = new Driver({
      allowClose: true,
      animate: !reduzirMovimento,
      smoothScroll: !reduzirMovimento,
      onHighlightStarted: async (element, _step, options) => {
        transicaoEmCurso = true
        const indiceAtual = options?.state?.activeIndex ?? driver.driverInstance.getActiveIndex?.() ?? 0
        if (indiceAtual !== ultimoIndiceDestacado) {
          ultimoIndiceDestacado = indiceAtual
          indiceJaReancorado = null
          envioTentado = null
        }
        const passoAtual = tour.passos[indiceAtual]
        if (!passoAtual) return

        if (fluxoDoPassoInterrompido(indiceAtual)) return

        const alvoEsperado = resolverElementoNoContexto(passoAtual.seletor_alvo)
        const elementoEhDummy = !element || element.id === 'driver-dummy-element'
        const dentroModal = dentroDeModal(alvoEsperado)

        if (elementoEhDummy || dentroModal) {
          const alvoResolvido = await reposicionarPasso(passoAtual, indiceAtual, elementoEhDummy)
          if (!alvoResolvido) {
            if (alvoPresenteMasInvisivel(passoAtual.seletor_alvo)) {
              interromperNoPasso(MOTIVOS_DE_INTERRUPCAO.ALVO_PRESENTE_MAS_INVISIVEL, indiceAtual)
            } else {
              interromperNoPasso(MOTIVOS_DE_INTERRUPCAO.ALVO_NAO_ENCONTRADO, indiceAtual)
              requestAnimationFrame(pularPasso)
            }
          }
        }
      },
      onHighlighted: (element, _step, options) => {
        const destacouElementoReal = Boolean(element && element.id !== 'driver-dummy-element')
        if (destacouElementoReal) refreshPendentePosTransicao = true
        encerrarJanelaTransicao()
        const indiceAtual = options?.state?.activeIndex ?? 0
        const passoAtual = tour.passos[indiceAtual]
        if (!passoAtual) return
        if (destacouElementoReal) {
          algumPassoDestacado = true
          removerFantasmaOrfao(element)
        }
        registrarListenerCliqueReverso(passoAtual, indiceAtual)
        if (!deveSuprimirSideEffects()) {
          salvarEstado(tour, indiceAtual)
        }
      },
      onPopoverRender: aoRenderizarBalao,
      onNextClick: async (_element, _step, options) => {
        if (aguardandoNavegacao) return
        const indiceAtual = options?.state?.activeIndex ?? 0
        if (envioEmAlerta(indiceAtual)) return
        acaoEmCurso = ACOES_DO_TOUR.PROXIMO
        const proximoIndice = indiceAtual + 1
        if (navegacaoDependeDaAcaoDoPasso(indiceAtual)) {
          await aguardarNavegacaoPelaAcao(indiceAtual)
          return
        }
        if (fluxoDoPassoInterrompido(proximoIndice)) return
        aguardandoNavegacao = true
        transicaoEmCurso = true
        try {
          alternarBotaoAguardando(SELETOR_BOTAO_PROXIMO, true)
          const passoAtual = tour.passos[indiceAtual]
          const passoDestino = tour.passos[proximoIndice]
          const envioEmModal = ehPassoDeEnvio(resolverElementoNoContexto(passoAtual?.seletor_alvo))
          const modaisAntes = modaisAbertasAgora()
          const disparouNaOrigem = dispararAcaoDoPasso(passoAtual)
          if (disparouNaOrigem) {
            await aguardarElementoVisivel(passoAtual.seletor_alvo, TEMPO_ESTABILIZACAO_POS_DISPARO_MS)
            registrarModalAbertaPeloTour(modaisAntes)
          }
          let alvoDestino = await aguardarAlvoDoPasso(passoDestino)
          if (disparouNaOrigem) registrarModalAbertaPeloTour(modaisAntes)
          const destinoEhUltimoPasso = proximoIndice >= tour.passos.length - 1
          let esperasRestantes = MAX_REPOSICIONAMENTOS_EM_MODAL
          while (!alvoDestino && algumaModalOcupandoATela() && esperasRestantes > 0) {
            if (envioEmModal && errosNovosDesdeOEnvio(resolverElementoNoContexto(passoAtual.seletor_alvo))) break
            esperasRestantes -= 1
            alvoDestino = await aguardarAlvoDoPasso(passoDestino)
          }
          if (!driver.driverInstance.isActive?.()) return
          if (envioEmModal && !avancoDoEnvioComprovado(passoAtual, passoDestino, alvoDestino)) return
          if (passoDestino?.seletor_alvo && !alvoDestino && destinoEhUltimoPasso) {
            encerrarTourPorAlvoInalcancavel(MOTIVOS_DE_INTERRUPCAO.ULTIMO_PASSO_SEM_ALVO, proximoIndice)
            return
          }
          driver.driverInstance.moveNext()
        } finally {
          alternarBotaoAguardando(SELETOR_BOTAO_PROXIMO, false)
          aguardandoNavegacao = false
          encerrarJanelaTransicao()
        }
      },
      onPrevClick: async (_element, _step, options) => {
        if (aguardandoNavegacao) return
        const indiceAtual = options?.state?.activeIndex ?? 0
        acaoEmCurso = ACOES_DO_TOUR.ANTERIOR
        desarmarVigiaFechamento()
        const anteriorIndice = indiceAtual - 1
        if (fluxoDoPassoInterrompido(anteriorIndice)) return
        aguardandoNavegacao = true
        transicaoEmCurso = true
        try {
          alternarBotaoAguardando(SELETOR_BOTAO_ANTERIOR, true)
          const passoAnterior = tour.passos[anteriorIndice]
          if (!alvoDoPassoEstaNaModalDoTour(passoAnterior)) fecharModalAbertaPeloTour()
          let alvoAnterior = await aguardarAlvoDoPasso(passoAnterior)
          if (passoAnterior?.seletor_alvo && !alvoAnterior) {
            alvoAnterior = await restaurarContextoDoPasso(anteriorIndice)
          }
          if (!driver.driverInstance.isActive?.()) return
          if (passoAnterior?.seletor_alvo && !alvoAnterior) {
            const destino = indiceAnteriorAlcancavel(anteriorIndice)
            if (destino === null) {
              encerrarTourPorAlvoInalcancavel(MOTIVOS_DE_INTERRUPCAO.NENHUM_PASSO_ANTERIOR_ALCANCAVEL, anteriorIndice)
              return
            }
            driver.driverInstance.moveTo(destino)
            return
          }
          driver.driverInstance.movePrevious()
        } finally {
          alternarBotaoAguardando(SELETOR_BOTAO_ANTERIOR, false)
          aguardandoNavegacao = false
          encerrarJanelaTransicao()
        }
      },
      onDestroyStarted: () => {
        if (navegando || deveSuprimirSideEffects()) {
          driver.driverInstance.destroy()
          return
        }
        const instance = driver.driverInstance
        if (abandonandoTour) {
          instance.destroy()
          return
        }
        if (!instance.hasNextStep()) {
          if (algumPassoDestacado) registrar(false)
        } else if (confirm('Deseja sair do tour?')) {
          registrar(true)
        } else {
          return
        }
        instance.destroy()
      },
      onDestroyed: () => {
        encerrarJanelaTransicao()
        aguardandoNavegacao = false
        limparListenerCliqueReverso()
        window.removeEventListener('pagehide', aoSairDaPagina)
        document.removeEventListener(EVENTO_ENVIO_FORMULARIO, aoEnviarFormulario, true)
        document.removeEventListener(EVENTO_ENVIO_BARRADO_PELO_NAVEGADOR, aoBarrarEnvioNoNavegador, true)
        desarmarVigiaFechamento()
        estadoEnvioRenderizado = null
        envioTentado = null
        if (observer) {
          observer.disconnect()
          observer = null
        }
        limparPopupsLiberados()
        instanciaAtiva = null
        if (!navegando && !deveSuprimirSideEffects()) limparEstado()
      }
    })

    const aoSairDaPagina = () => {
      if (registrado || deveSuprimirSideEffects()) return
      if (!driver.driverInstance.isActive?.()) return
      const indiceAtual = driver.driverInstance.getActiveIndex?.() ?? 0
      salvarEstado(tour, indiceAtual)
    }
    window.addEventListener('pagehide', aoSairDaPagina)
    document.addEventListener(EVENTO_ENVIO_FORMULARIO, aoEnviarFormulario, true)
    document.addEventListener(EVENTO_ENVIO_BARRADO_PELO_NAVEGADOR, aoBarrarEnvioNoNavegador, true)

    instanciaAtiva = driver.driverInstance

    const inicio = typeof indiceInicial === 'number' && indiceInicial > 0 ? indiceInicial : 0
    const situacaoRotaInicial = resolverRotaDoPasso(inicio)

    if (situacaoRotaInicial !== SITUACAO_ROTA_ATUAL) {
      window.removeEventListener('pagehide', aoSairDaPagina)
      document.removeEventListener(EVENTO_ENVIO_FORMULARIO, aoEnviarFormulario, true)
      instanciaAtiva = null
      if (situacaoRotaInicial === SITUACAO_ROTA_INALCANCAVEL) limparEstado()
      return
    }

    if (!deveSuprimirSideEffects() && carregarEstado()?.navegacao_pelo_tour) salvarEstado(tour, inicio)

    const passos = montarPassosDriver(tour.passos)
    const passoInicial = tour.passos[inicio]
    const iniciar = (indice) => {
      driver.tour(passos, indice)
      observer = instalarRefreshEmMutacoes(driver.driverInstance, janelaTransicao, reancorarPasso)
    }
    if (passoInicial?.seletor_alvo) {
      aguardarAlvoDoPasso(passoInicial)
        .then((alvo) => (alvo || inicio === 0 ? inicio : indiceParaRetomar(inicio)))
        .then(iniciar)
        .catch((erro) => encerrarPorFalhaAoIniciar(erro, { tour, indicePasso: inicio, acao: acaoEmCurso }))
    } else {
      iniciar(inicio)
    }
  }

  function executarTour(tour) {
    const estado = carregarEstado()
    const indiceCasado = estado?.tour_id === tour.id ? indiceRetomavel(estado) : null
    if (indiceCasado !== null) {
      retomarTour({ ...estado, indice_passo: indiceCasado })
      return
    }
    limparEstado()
    const indiceInicial = typeof tour.indice_inicial === 'number' ? tour.indice_inicial : 0
    iniciarDriver(tour, indiceInicial, ACOES_DO_TOUR.INICIO)
  }

  function retomarTour(estado) {
    const tour = {
      id: estado.tour_id,
      passos: estado.passos,
      rota_pattern: estado.rota_pattern,
      nome: estado.nome
    }
    iniciarDriver(tour, estado.indice_passo, ACOES_DO_TOUR.RETOMADA)
  }

  return {
    executarTour,
    retomarTour,
    marcarConclusao,
    salvarEstado,
    carregarEstado,
    limparEstado,
    tentativasDeNavegacao,
    registrarTentativaNavegacao,
    rotaInternaValida,
    rotaContemCuringa,
    builderAtivo,
    encerrarSilenciosamente
  }
}
